import { useMemo } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import { startOfWeek, addDays, format } from 'date-fns';
import { useSchedules, useSites } from '@/api/resources';
import { useCurrentUser } from '@/api/auth';

// Le dimanche n'apparaît que si l'officine ouvre ce jour-là.
const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export default function MyWeekPage() {
  const { data: user } = useCurrentUser();
  // On passe par `/sites`, qui ne dit que « ouvre le dimanche ou non », plutôt
  // que par la fiche pharmacie complète : un salarié n'a pas à télécharger les
  // règles de primes et les effectifs requis pour afficher sa semaine.
  const { data: sites } = useSites();
  const dayCount = useMemo(
    () => (sites?.sites?.some((s) => s.opensOnSunday) ? 7 : 6),
    [sites]
  );
  const weekStart = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 1 }), []);
  const weekEnd = useMemo(() => addDays(weekStart, dayCount - 1), [weekStart, dayCount]);

  const { data: schedules, isLoading } = useSchedules(
    format(weekStart, 'yyyy-MM-dd'),
    format(weekEnd, 'yyyy-MM-dd'),
    user?.employeeId ?? undefined
  );

  const schedulesByDay = useMemo(() => {
    const map = new Map<string, NonNullable<typeof schedules>[number]>();
    schedules?.forEach((s) => map.set(s.date.slice(0, 10), s));
    return map;
  }, [schedules]);

  return (
    <div className="space-y-5">
      <div>
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-medium mb-1">
          Bonjour {user?.employee?.firstName ?? ''} 👋
        </div>
        <h2 className="text-[26px] font-semibold tracking-tight leading-tight">
          Votre semaine du{' '}
          <span className="text-primary">
            {format(weekStart, 'd MMM')} au {format(weekEnd, 'd MMM yyyy')}
          </span>
        </h2>
        <PageHelp topic="myWeek" />
      </div>

      {isLoading && <div className="text-[12px] text-slate-500">Chargement…</div>}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid border-b border-slate-200" style={{ gridTemplateColumns: `repeat(${dayCount}, minmax(0, 1fr))` }}>
          {DAYS.slice(0, dayCount).map((d, i) => {
            const date = addDays(weekStart, i);
            const dayKey = format(date, 'yyyy-MM-dd');
            const s = schedulesByDay.get(dayKey);
            const isWork = s?.status === 'WORK';

            return (
              <div key={d} className="px-3 py-3 border-r border-slate-200 last:border-r-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{d}</div>
                <div className="text-[15px] font-semibold tabular-nums mt-0.5">{format(date, 'd')}</div>
                {isWork ? (
                  <div className="mt-2 space-y-1">
                    {s?.morningStart && (
                      <div className="text-[10px] text-primary bg-primary-bg rounded px-1.5 py-0.5 tabular-nums font-medium">
                        {s.morningStart}–{s.morningEnd}
                      </div>
                    )}
                    {s?.afternoonStart && (
                      <div className="text-[10px] text-primary bg-primary-bg rounded px-1.5 py-0.5 tabular-nums font-medium">
                        {s.afternoonStart}–{s.afternoonEnd}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-2 text-[10px] text-slate-400 italic">
                    {s?.status === 'LEAVE' ? 'Congé' : s?.status === 'SICK' ? 'Arrêt' : 'Repos'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
